import { LitElement, html, nothing } from 'lit';
import { isEventDone } from '../goals';
import type { CalendarEvent } from '../types';
import { t } from '../i18n';
import { defineZenComponent } from './register';

export class ZenTodayEvents extends LitElement {
  static properties = {
    events: { attribute: false },
    meetingMinutes: { attribute: false },
    doneLabel: { attribute: false },
  };

  declare events: CalendarEvent[];
  declare meetingMinutes: number;
  declare doneLabel: string;

  constructor() {
    super();
    this.events = [];
    this.meetingMinutes = 0;
    this.doneLabel = '';
  }

  createRenderRoot() {
    return this;
  }

  render() {
    const now = new Date();
    const incomplete = this.events.filter((ev) => !isEventDone(ev, now));
    const done = this.events.filter((ev) => isEventDone(ev, now));
    const fmtDur = (min: number) => min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? `${min % 60}m` : ''}` : `${min}m`;

    return html`
      ${this.meetingMinutes > 0
        ? html`
            <div class="events-summary">
              <span class="mi mi-xs">groups</span>
              MTG ${fmtDur(this.meetingMinutes)}
            </div>
          `
        : nothing}
      ${incomplete.map((ev) => this.renderEventItem(ev, now))}
      ${done.length > 0 ? html`
        <div class="events-done-section">
          <button class="events-done-toggle"><span class="mi mi-xs">expand_more</span> ${this.doneLabel}</button>
          <div class="events-done-list" style="display:none">${done.map((ev) => this.renderEventItem(ev, now))}</div>
        </div>
      ` : nothing}
    `;
  }

  private renderEventItem(ev: CalendarEvent, now: Date) {
    const isDone = isEventDone(ev, now);
    const tentative = ev.status === 'tentative';
    const classes = `event-item${tentative ? ' event-status-tentative' : ''}${isDone ? ' event-item-done' : ''}`;
    const timeBadge = this.renderTimeBadge(ev);
    return html`
      <div class=${classes} data-event-id=${ev.id}>
        <button
          class=${`event-done-btn${isDone ? ' checked' : ''}`}
          data-event-done-id=${ev.id}
          title=${isDone ? String(t('markUndoneTitle')) : String(t('markDoneTitle'))}
        >
          ${isDone ? html`<span class="mi mi-xs">check</span>` : nothing}
        </button>
        <span class="event-item-title">${ev.title}</span>
        ${timeBadge}
      </div>
    `;
  }

  private renderTimeBadge(ev: CalendarEvent) {
    if (ev.is_all_day) return html`<span class="event-time-badge">${String(t('allDay'))}</span>`;
    if (!ev.start_at) return nothing;
    const start = new Date(ev.start_at);
    const end = ev.end_at ? new Date(ev.end_at) : null;
    const fmt = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return html`<span class="event-time-badge">${fmt(start)}${end ? `〜${fmt(end)}` : ''}</span>`;
  }
}

defineZenComponent('zen-today-events', ZenTodayEvents);
